import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function formatFecha(fechaStr) {
  if (!fechaStr) return '';
  try {
    return new Date(fechaStr).toLocaleDateString('es-CL', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return fechaStr; }
}

const TIPOS_CANCHA  = ['Fútbol 11', 'Fútbol 7', 'Fútbol 5', 'Pádel', 'Tenis', 'Básquetbol'];
const SUPERFICIES   = ['Pasto sintético', 'Pasto natural', 'Cemento', 'Madera', 'Arcilla'];
const DIAS_SEMANA   = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];
const HORAS_DIA     = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00',
                       '16:00','17:00','18:00','19:00','20:00','21:00','22:00'];

export default function PanelRecinto() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const [recinto,     setRecinto]     = useState(null);
  const [canchas,     setCanchas]     = useState([]);
  const [partidos,    setPartidos]    = useState([]);
  const [cargando,    setCargando]    = useState(true);
  const [editando,    setEditando]    = useState(false);
  const [form,        setForm]        = useState({});
  const [guardando,   setGuardando]   = useState(false);
  const [error,       setError]       = useState('');
  const [modalCancha, setModalCancha] = useState(null);
  const [formCancha,  setFormCancha]  = useState({});

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!user?.id) return;
    cargarTodo();
  }, [user]);

  const cargarTodo = async () => {
    setCargando(true);
    try {
      const resRecintos   = await fetch(`${API}/api/recintos`);
      const todosRecintos = await resRecintos.json();
      const miRecinto     = todosRecintos.find(r => r.admin_id === user.id);
      if (!miRecinto) { setCargando(false); return; }
      setRecinto(miRecinto);
      setForm(miRecinto);

      const [resCanchas, resPartidos] = await Promise.all([
        fetch(`${API}/api/canchas/recinto/${miRecinto.id}`),
        fetch(`${API}/api/recintos/${miRecinto.id}/partidos`),
      ]);
      const canchasData = await resCanchas.json();
      if (Array.isArray(canchasData)) setCanchas(canchasData);
      if (resPartidos.ok) {
        const partidosData = await resPartidos.json();
        if (Array.isArray(partidosData)) setPartidos(partidosData);
      }
    } catch { }
    finally { setCargando(false); }
  };

  const handleGuardar = async () => {
    if (!recinto?.id) return;
    setGuardando(true); setError('');
    try {
      const res = await fetch(`${API}/api/recintos/${recinto.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          nombre: form.nombre, direccion: form.direccion, ciudad: form.ciudad,
          zona: form.zona, descripcion: form.descripcion,
          instagram: form.instagram, facebook: form.facebook,
          tiktok: form.tiktok, web: form.web,
          dias_activos: form.dias_activos, apertura: form.apertura, cierre: form.cierre,
          servicios: form.servicios || [],
        }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setRecinto(data); setEditando(false);
    } catch { setError('Error de conexión'); }
    finally { setGuardando(false); }
  };

  const handleGuardarCancha = async () => {
    if (!formCancha.nombre || !formCancha.tipo || !formCancha.precio) {
      setError('Nombre, tipo y precio son obligatorios'); return;
    }
    setGuardando(true); setError('');
    try {
      const esNueva = modalCancha === 'nueva';
      const url     = esNueva ? `${API}/api/canchas` : `${API}/api/canchas/${modalCancha.id}`;
      const body    = esNueva ? { ...formCancha, recinto_id: recinto.id } : formCancha;
      const res     = await fetch(url, {
        method: esNueva ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setCanchas(prev => esNueva ? [...prev, data] : prev.map(c => c.id === data.id ? data : c));
      setModalCancha(null); setFormCancha({}); setError('');
    } catch { setError('Error de conexión'); }
    finally { setGuardando(false); }
  };

  const toggleDia = (dia) => setForm(p => ({
    ...p,
    dias_activos: (p.dias_activos || []).includes(dia)
      ? (p.dias_activos || []).filter(d => d !== dia)
      : [...(p.dias_activos || []), dia],
  }));

  const toggleTipoRecinto = (tipo) => setForm(p => ({
    ...p,
    servicios: (p.servicios || []).includes(tipo)
      ? (p.servicios || []).filter(t => t !== tipo)
      : [...(p.servicios || []), tipo],
  }));

  const toggleHoraCancha = (hora) => setFormCancha(p => ({
    ...p,
    horas_disponibles: (p.horas_disponibles || []).includes(hora)
      ? (p.horas_disponibles || []).filter(h => h !== hora)
      : [...(p.horas_disponibles || []), hora],
  }));

  if (cargando) return (
    <div style={styles.pagina}>
      <div style={styles.cargando}>Cargando panel...</div>
    </div>
  );

  if (!recinto) return (
    <div style={styles.pagina}>
      <div style={styles.sinRecinto}>
        <div style={styles.sinRecintoIcono}>🏟️</div>
        <div style={styles.sinRecintoTitulo}>No tienes un recinto registrado</div>
        <div style={styles.sinRecintoSub}>Registra tu recinto para recibir partidos.</div>
        <button style={styles.btnNaranja} onClick={() => navigate('/registro-recinto')}>
          Registrar mi recinto →
        </button>
      </div>
    </div>
  );

  const proximosPartidos = partidos.filter(p => p.estado === 'confirmado' || p.estado === 'aceptado');
  const jugados          = partidos.filter(p => p.estado === 'jugado');
  const tiposRecinto     = (recinto.servicios || []).filter(s => TIPOS_CANCHA.includes(s));

  return (
    <div style={styles.pagina}>

      {/* ── HEADER ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.avatar}>🏟️</div>
          <div>
            <div style={styles.titulo}>{recinto.nombre}</div>
            <div style={styles.sub}>
              {recinto.direccion}{recinto.ciudad ? `, ${recinto.ciudad}` : ''}
              {recinto.zona ? ` · ${recinto.zona}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={styles.rolBadge}>Admin Recinto · TuRival</span>
              {tiposRecinto.map(t => (
                <span key={t} style={styles.tipoBadge}>{t}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.btnEditar} onClick={() => { setEditando(!editando); setError(''); }}>
            {editando ? 'Cancelar' : '✏️ Editar'}
          </button>
          <button style={styles.btnSalir} onClick={() => { logout(); navigate('/login'); }}>
            Salir
          </button>
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={styles.statsRow}>
        {[
          { num: canchas.length,          label: 'Canchas',   color: 'white'    },
          { num: proximosPartidos.length, label: 'Próximos',  color: '#FF6B1A'  },
          { num: jugados.length,          label: 'Jugados',   color: '#16A34A'  },
          { num: recinto.rating || '—',   label: 'Rating ⭐', color: '#F59E0B'  },
        ].map(({ num, label, color }) => (
          <div key={label} style={styles.statBox}>
            <div style={{ ...styles.statNum, color }}>{num}</div>
            <div style={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* ── GRID 3 COLUMNAS ── */}
      <div style={styles.grid}>

        {/* COL 1 — Datos del recinto */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Datos del recinto</div>
          {!editando ? (
            <div>
              {[
                { l: 'Horario', v: `${recinto.apertura || '--'} – ${recinto.cierre || '--'}` },
                { l: 'Instagram', v: recinto.instagram || '--' },
                { l: 'Facebook',  v: recinto.facebook  || '--' },
                { l: 'Web',       v: recinto.web        || '--' },
              ].map(({ l, v }) => (
                <div key={l} style={styles.infoRow}>
                  <span style={styles.infoLabel}>{l}</span>
                  <span style={styles.infoVal}>{v}</span>
                </div>
              ))}
              {recinto.descripcion && (
                <div style={styles.descripcionVista}>{recinto.descripcion}</div>
              )}
              <div style={{ marginTop: 12 }}>
                <div style={styles.editLabel}>Días activos</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  {(recinto.dias_activos || []).length === 0
                    ? <span style={styles.sinDatos}>Sin días definidos</span>
                    : (recinto.dias_activos || []).map(d => (
                        <span key={d} style={styles.chipVista}>{d.slice(0,3)}</span>
                      ))
                  }
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={styles.editLabel}>Tipos de cancha</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  {tiposRecinto.length === 0
                    ? <span style={styles.sinDatos}>Sin tipos definidos</span>
                    : tiposRecinto.map(t => (
                        <span key={t} style={{ ...styles.chipVista, background: 'rgba(255,107,26,0.2)', color: '#FF6B1A' }}>{t}</span>
                      ))
                  }
                </div>
              </div>
            </div>
          ) : (
            <div>
              {[
                { key: 'nombre',      label: 'Nombre'                      },
                { key: 'direccion',   label: 'Dirección'                   },
                { key: 'ciudad',      label: 'Ciudad'                      },
                { key: 'zona',        label: 'Zona'                        },
                { key: 'descripcion', label: 'Descripción'                 },
                { key: 'apertura',    label: 'Apertura (ej: 08:00)'        },
                { key: 'cierre',      label: 'Cierre (ej: 23:00)'          },
                { key: 'instagram',   label: 'Instagram'                   },
                { key: 'facebook',    label: 'Facebook'                    },
                { key: 'tiktok',      label: 'TikTok'                      },
                { key: 'web',         label: 'Sitio web'                   },
              ].map(({ key, label }) => (
                <div key={key} style={{ marginBottom: 9 }}>
                  <label style={styles.editLabel}>{label}</label>
                  <input value={form[key] || ''}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    style={styles.editInput} />
                </div>
              ))}
              {/* Días activos */}
              <div style={{ marginBottom: 10 }}>
                <label style={styles.editLabel}>Días activos</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                  {DIAS_SEMANA.map(d => {
                    const activo = (form.dias_activos || []).includes(d);
                    return (
                      <div key={d} onClick={() => toggleDia(d)}
                        style={{ ...styles.chip, background: activo ? '#FF6B1A' : 'rgba(255,255,255,0.06)', color: activo ? 'white' : 'rgba(255,255,255,0.4)' }}>
                        {d.slice(0,3)}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Tipos de cancha */}
              <div style={{ marginBottom: 12 }}>
                <label style={styles.editLabel}>Tipos de cancha ofrecidos</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                  {TIPOS_CANCHA.map(t => {
                    const activo = (form.servicios || []).includes(t);
                    return (
                      <div key={t} onClick={() => toggleTipoRecinto(t)}
                        style={{ ...styles.chip, background: activo ? '#FF6B1A' : 'rgba(255,255,255,0.06)', color: activo ? 'white' : 'rgba(255,255,255,0.4)' }}>
                        {t}
                      </div>
                    );
                  })}
                </div>
              </div>
              <button style={{ ...styles.btnNaranja, width: '100%', opacity: guardando ? 0.7 : 1 }}
                onClick={handleGuardar} disabled={guardando}>
                {guardando ? 'Guardando...' : '✓ Guardar cambios'}
              </button>
            </div>
          )}
        </div>

        {/* COL 2 — Canchas */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={styles.cardTitle}>Mis canchas</div>
            <button style={styles.btnAgregar} onClick={() => { setModalCancha('nueva'); setFormCancha({}); setError(''); }}>
              + Agregar
            </button>
          </div>
          {canchas.length === 0 ? (
            <div style={styles.sinDatos}>No tienes canchas registradas.</div>
          ) : (
            canchas.map(c => (
              <div key={c.id} style={styles.canchaCard}>
                <div style={{ flex: 1 }}>
                  <div style={styles.canchaNombre}>{c.nombre}</div>
                  <div style={styles.canchaDetalle}>
                    {c.tipo} · {c.superficie || 'Sin superficie'} · {c.techada ? '🏠 Techada' : '☀️ Al aire libre'}
                  </div>
                  <div style={{ ...styles.canchaDetalle, color: '#FF6B1A', fontWeight: 700, marginTop: 2 }}>
                    ${Number(c.precio).toLocaleString('es-CL')} / hora
                  </div>
                  {/* Horarios disponibles */}
                  {(c.horas_disponibles || []).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif", marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Horarios disponibles
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {[...(c.horas_disponibles || [])].sort().map(h => (
                          <span key={h} style={{ background: 'rgba(26,107,255,0.2)', color: '#60a5fa', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, fontFamily: "'Barlow', sans-serif" }}>
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button style={styles.btnEditarChico}
                  onClick={() => { setModalCancha(c); setFormCancha(c); setError(''); }}>
                  Editar
                </button>
              </div>
            ))
          )}
        </div>

        {/* COL 3 — Partidos */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Partidos agendados</div>
          {proximosPartidos.length === 0 ? (
            <div style={styles.sinDatos}>No hay partidos próximos.</div>
          ) : (
            proximosPartidos.map(p => (
              <div key={p.id} style={styles.partidoCard}>
                <div style={styles.partidoVs}>{p.local_nombre} vs {p.visitante_nombre}</div>
                <div style={styles.partidoMeta}>
                  📅 {formatFecha(p.fecha)} · {p.hora}
                  {p.cancha_nombre && ` · ${p.cancha_nombre}`}
                </div>
                <div style={{
                  ...styles.estadoBadge,
                  background: p.estado === 'confirmado' ? 'rgba(22,163,74,0.15)' : 'rgba(245,158,11,0.15)',
                  color: p.estado === 'confirmado' ? '#16A34A' : '#F59E0B',
                }}>
                  {p.estado === 'confirmado' ? '✅ Confirmado' : '⏳ Sin árbitro'}
                </div>
              </div>
            ))
          )}

          {jugados.length > 0 && (
            <>
              <div style={{ ...styles.cardTitle, marginTop: 16 }}>Historial</div>
              {jugados.slice(0, 5).map(p => (
                <div key={p.id} style={styles.partidoCard}>
                  <div style={styles.partidoVs}>{p.local_nombre} vs {p.visitante_nombre}</div>
                  <div style={styles.partidoMeta}>📅 {formatFecha(p.fecha)} · {p.hora}</div>
                  <div style={{ ...styles.estadoBadge, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                    Jugado
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

      </div>

      {/* ── MODAL CANCHA ── */}
      {modalCancha && (
        <div style={styles.modalOverlay} onClick={() => { setModalCancha(null); setError(''); }}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitulo}>
                {modalCancha === 'nueva' ? 'Nueva cancha' : `Editar ${modalCancha.nombre}`}
              </div>
              <button style={styles.btnCerrar} onClick={() => { setModalCancha(null); setError(''); }}>✕</button>
            </div>
            {error && <div style={styles.errorModal}>{error}</div>}

            {/* Nombre y precio */}
            {[
              { key: 'nombre', label: 'Nombre', placeholder: 'Cancha 1' },
              { key: 'precio', label: 'Precio por hora ($)', placeholder: '15000', type: 'number' },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={styles.editLabel}>{label}</label>
                <input type={type || 'text'} value={formCancha[key] || ''}
                  onChange={e => setFormCancha(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder} style={styles.editInput} />
              </div>
            ))}

            {/* Tipo — select oscuro */}
            <div style={{ marginBottom: 12 }}>
              <label style={styles.editLabel}>Tipo</label>
              <select value={formCancha.tipo || ''}
                onChange={e => setFormCancha(p => ({ ...p, tipo: e.target.value }))}
                style={styles.editSelect}>
                <option value="">Seleccionar...</option>
                {TIPOS_CANCHA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Superficie — select oscuro */}
            <div style={{ marginBottom: 12 }}>
              <label style={styles.editLabel}>Superficie</label>
              <select value={formCancha.superficie || ''}
                onChange={e => setFormCancha(p => ({ ...p, superficie: e.target.value }))}
                style={styles.editSelect}>
                <option value="">Seleccionar...</option>
                {SUPERFICIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Techada */}
            <div style={{ marginBottom: 12 }}>
              <label style={styles.editLabel}>Techada</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
                {[true, false].map(v => (
                  <div key={String(v)} onClick={() => setFormCancha(p => ({ ...p, techada: v }))}
                    style={{ ...styles.chip, flex: 1, textAlign: 'center',
                      background: formCancha.techada === v ? '#FF6B1A' : 'rgba(255,255,255,0.06)',
                      color: formCancha.techada === v ? 'white' : 'rgba(255,255,255,0.4)' }}>
                    {v ? '🏠 Sí' : '☀️ No'}
                  </div>
                ))}
              </div>
            </div>

            {/* Horarios disponibles */}
            <div style={{ marginBottom: 16 }}>
              <label style={styles.editLabel}>Horarios disponibles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                {HORAS_DIA.map(h => {
                  const activo = (formCancha.horas_disponibles || []).includes(h);
                  return (
                    <div key={h} onClick={() => toggleHoraCancha(h)}
                      style={{ ...styles.chip,
                        background: activo ? '#1A6BFF' : 'rgba(255,255,255,0.06)',
                        color: activo ? 'white' : 'rgba(255,255,255,0.4)',
                        fontSize: 11, padding: '5px 10px' }}>
                      {h}
                    </div>
                  );
                })}
              </div>
            </div>

            <button style={{ ...styles.btnNaranja, width: '100%', opacity: guardando ? 0.7 : 1 }}
              onClick={handleGuardarCancha} disabled={guardando}>
              {guardando ? 'Guardando...' : '✓ Guardar cancha'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  pagina:         { minHeight: '100vh', background: '#0D1117', padding: '20px' },
  cargando:       { textAlign: 'center', padding: '80px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  header:         { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '16px 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft:     { display: 'flex', alignItems: 'center', gap: 14 },
  avatar:         { fontSize: 36 },
  titulo:         { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 800, color: 'white', marginBottom: 2 },
  sub:            { fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  rolBadge:       { background: 'rgba(255,107,26,0.2)', color: '#FF6B1A', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  tipoBadge:      { background: 'rgba(26,107,255,0.2)', color: '#60a5fa', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  headerRight:    { display: 'flex', gap: 8 },
  btnEditar:      { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '7px 14px', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnSalir:       { background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '7px 14px', color: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  statsRow:       { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 },
  statBox:        { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px', textAlign: 'center' },
  statNum:        { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 26, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 3 },
  statLabel:      { fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Barlow', sans-serif" },
  errorBanner:    { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#EF4444', fontFamily: "'Barlow', sans-serif", marginBottom: 12 },
  grid:           { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, alignItems: 'start' },
  card:           { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16 },
  cardTitle:      { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  infoRow:        { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  infoLabel:      { fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif" },
  infoVal:        { fontSize: 11, color: 'white', fontFamily: "'Barlow', sans-serif", fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' },
  descripcionVista: { marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", lineHeight: 1.5 },
  editLabel:      { display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontFamily: "'Barlow', sans-serif" },
  editInput:      { width: '100%', padding: '8px 11px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12, color: 'white', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  editSelect:     { width: '100%', padding: '8px 11px', background: '#1E2738', border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12, color: 'white', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
  chip:           { padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Barlow', sans-serif" },
  chipVista:      { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  sinDatos:       { fontSize: 12, color: 'rgba(255,255,255,0.25)', fontFamily: "'Barlow', sans-serif", fontStyle: 'italic' },
  btnNaranja:     { padding: '10px', background: '#FF6B1A', border: 'none', borderRadius: 10, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 800, color: 'white', cursor: 'pointer' },
  btnAgregar:     { background: 'rgba(255,107,26,0.15)', border: '1px solid rgba(255,107,26,0.3)', borderRadius: 8, padding: '4px 10px', color: '#FF6B1A', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnEditarChico: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '4px 10px', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif", flexShrink: 0 },
  canchaCard:     { display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 11, marginBottom: 8 },
  canchaNombre:   { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: 'white', marginBottom: 2 },
  canchaDetalle:  { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  partidoCard:    { background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 11, marginBottom: 8 },
  partidoVs:      { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: 'white', marginBottom: 3 },
  partidoMeta:    { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", marginBottom: 5 },
  estadoBadge:    { display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  sinRecinto:     { maxWidth: 440, margin: '80px auto', background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '48px 40px', textAlign: 'center' },
  sinRecintoIcono:  { fontSize: 52, marginBottom: 14 },
  sinRecintoTitulo: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 800, color: 'white', marginBottom: 8 },
  sinRecintoSub:    { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", marginBottom: 20 },
  modalOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal:          { background: '#161C26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 },
  modalTitulo:    { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20, fontWeight: 800, color: 'white' },
  btnCerrar:      { background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, width: 30, height: 30, fontSize: 13, color: 'rgba(255,255,255,0.5)', cursor: 'pointer' },
  errorModal:     { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#EF4444', fontFamily: "'Barlow', sans-serif", marginBottom: 12 },
};
